import { useState } from 'react';
import Image from "next/legacy/image";
import {
  CirleDownAngleIcon,
  CrossIcon,
  HamburgerIcon,
  HeaderDropdownMenu,
  HnaviHeaderSpMenu,
  NestedMenuItemData,
  RightTriangleIcon,
} from '@components';
import HnaviLogo from '@images/hnavi-logo.png';
import { hnaviAreas, hnaviSearchMethod, hnaviSearchName } from '@constants';
import {
  basePath,
  beginnersPath,
  casestudyListPath,
  developerEntryPath,
  hnaviCasePath,
  hnaviCategoryPath,
  hnaviSearchPath,
  buyerContactPath,
  adSaasPath,
} from '@paths';
import Wakaba from '@images/icon-wakaba.png';
import FormSearchIcon from '@images/icon-search-s.svg';
import { addTrailingSlash } from '@utils';

const areaMenu: NestedMenuItemData = {
  label: 'エリアから選ぶ',
  children: Object.entries(hnaviAreas).map(([group, areas]) => ({
    label: group,
    children: areas.map((area) => ({
      label: area.name,
      href: hnaviSearchPath(area.code),
    })),
  })),
};

const spMenuItems = ({
  hnaviContentCategories,
}: {
  hnaviContentCategories?: { name: string; code: string }[];
}): NestedMenuItemData[] => [
  {
    label: '開発会社を探す',
    children: [
      {
        label: 'カテゴリから選ぶ',
        children: (hnaviContentCategories || []).map(({ code, name }) => ({
          label: name,
          href: hnaviCategoryPath(code),
        })),
      },
      { label: '対応技術から探す', href: hnaviSearchPath() },
      areaMenu,
    ],
  },
  { label: '発注ラウンジ', href: basePath },
  { label: '開発会社の方', href: developerEntryPath },
  { label: 'SaaSベンダーの方', href: adSaasPath },
  { label: '発注事例', href: casestudyListPath() },
];

const headerMenuItems = ({
  hnaviContentCategories,
}: {
  hnaviContentCategories?: { name: string; code: string }[];
}): NestedMenuItemData[] => [
  {
    label: 'カテゴリから選ぶ',
    children: (hnaviContentCategories || []).map(({ code, name }) => ({
      label: name,
      href: hnaviCategoryPath(code),
    })),
  },
  { label: 'システムから選ぶ', href: hnaviSearchPath() },
  areaMenu,
  { label: 'スキルから選ぶ', href: hnaviSearchPath() },
  {
    label: '実績から選ぶ',
    children: (hnaviContentCategories || []).map(({ code, name }) => ({
      label: name,
      href: hnaviCasePath(code),
    })),
  },
];

const HnaviHeaderSearchBox = () => (
  <form
    className='relative'
    method={hnaviSearchMethod}
    action={hnaviSearchPath()}
  >
    <input
      name={hnaviSearchName}
      className='placeholder-gray-400 text-gray-900 w-full appearance-none rounded-lg bg-white py-3 pl-4 pr-12 focus:outline-none'
      aria-label='search'
    />
    <button className='absolute bottom-0 right-3 top-0 w-5 cursor-pointer'>
      <img
        src={FormSearchIcon.src}
        alt='search'
        width={FormSearchIcon.width}
        height={FormSearchIcon.height}
        loading='lazy'
      />
    </button>
  </form>
);

const SpMenu: React.FC<{
  hnaviContentCategories?: { name: string; code: string }[];
}> = ({ hnaviContentCategories }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className='ml-auto w-8 text-top-primary lg:hidden'
        onClick={() => setOpen((prev) => !prev)}
        aria-label='メニュー'
      >
        {open ? <CrossIcon /> : <HamburgerIcon />}
      </button>
      <div className={open ? '' : 'hidden'}>
        <HnaviHeaderSpMenu items={spMenuItems({ hnaviContentCategories })} />
      </div>
    </>
  );
};

const SearchMenu: React.FC<{
  hnaviContentCategories?: { name: string; code: string }[];
}> = ({ hnaviContentCategories }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div
        className='mr-4 flex cursor-pointer items-center gap-1 hover:opacity-80'
        onClick={() => setOpen((prev) => !prev)}
      >
        発注先を探す
        <CirleDownAngleIcon className={`w-5 ${open ? 'rotate-180' : ''}`} />
      </div>
      <div className='relative'>
        <HeaderDropdownMenu
          hidden={!open}
          items={headerMenuItems({ hnaviContentCategories })}
        />
      </div>
    </div>
  );
};

export const HnaviHeader: React.FC<{
  hnaviContentCategories?: { name: string; code: string }[];
}> = ({ hnaviContentCategories }) => {
  return (
    <>
      <div className='fixed inset-x-0 top-0 z-10 h-16 whitespace-nowrap bg-lightgray shadow-md lg:h-24'>
        <div className='fw-bold container mx-auto flex h-full items-center font-bold'>
          <a href='/' className='relative w-40 shrink-0 lg:w-60'>
            <Image
              loader={(p) => p.src}
              src={HnaviLogo}
              alt='発注ナビ'
              height={HnaviLogo.height}
              width={HnaviLogo.width}
              loading='lazy'
            />
          </a>
          <div className='ml-1 hidden w-full items-center lg:flex'>
            <a
              href={addTrailingSlash(beginnersPath)}
              className='mr-4 flex items-center gap-1 hover:opacity-80'
            >
              初めての方
              <div className='h-[17px] w-4 shrink-0'>
                <Image
                  src={Wakaba}
                  alt='若葉マーク'
                  width={Wakaba.width}
                  height={Wakaba.height}
                />
              </div>
            </a>

            <SearchMenu hnaviContentCategories={hnaviContentCategories} />

            <a
              href={addTrailingSlash(casestudyListPath())}
              className='mr-6 hover:opacity-80'
            >
              発注事例
            </a>

            <a
              href={addTrailingSlash(developerEntryPath)}
              className='mr-4 flex items-center gap-1 text-top-primary hover:text-primary'
            >
              <RightTriangleIcon className='h-4' />
              開発会社の方
            </a>

            <a
              href={addTrailingSlash(adSaasPath)}
              className='mr-4 flex items-center gap-1 text-top-primary hover:text-primary'
            >
              <RightTriangleIcon className='h-4' />
              SaaSベンダーの方
            </a>
            <div className='ml-auto mr-4'>
              <HnaviHeaderSearchBox />
            </div>

            <a
              href={addTrailingSlash(buyerContactPath)}
              className='rounded-lg bg-top-primary px-4 py-3 font-bold text-white hover:opacity-75'
            >
              発注先のご相談
            </a>
          </div>
          <SpMenu hnaviContentCategories={hnaviContentCategories} />
        </div>
      </div>
      <div className='mb-4 h-16 lg:mb-8 lg:h-24' />
    </>
  );
};
